import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ErrorScreen from './ErrorScreen';
import Icon from './Icons';
import MarkAcquiredModal from './MarkAcquiredModal';
import { photoUrl } from './photoUtils';
import { plantDisplayName } from './nameUtils';

function formatCurrency(value) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return new Intl.NumberFormat(undefined, {
    style: 'currency', currency: 'USD', maximumFractionDigits: 2,
  }).format(Number(value));
}

const WISHLIST_PRIORITY_LABELS = {
  1: 'Top priority',
  2: 'High priority',
  3: 'Medium priority',
  4: 'Low priority',
  5: 'Backburner',
};

function formatWishlistPriority(value) {
  if (value == null || value === '') return 'Unspecified';
  return WISHLIST_PRIORITY_LABELS[Number(value)] || `Priority ${value}`;
}

function WishlistAppHeader({ count }) {
  return (
    <header className="app-header">
      <div className="app-header-brand">
        <img src="/logo_only.svg" alt="Aroidex" className="app-header-logo" />
        <div>
          <h1>Wishlist</h1>
          {count != null && (
            <p className="plant-count">{count} item{count === 1 ? '' : 's'}</p>
          )}
        </div>
      </div>
      <div className="app-header-actions">
        <Link to="/new/wishlist" className="btn btn-wishlist">+ Wishlist Plant</Link>
      </div>
    </header>
  );
}

function Wishlist() {
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [conversionTarget, setConversionTarget] = useState(null);

  const loadWishlist = () => {
    setLoading(true);
    setError(null);
    fetch('/api/wishlist')
      .then(r => {
        if (!r.ok) throw new Error('Failed to load wishlist');
        return r.json();
      })
      .then(data => {
        setPlants(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  };

  useEffect(() => {
    loadWishlist();
  }, []);

  if (loading) return (
    <div className="app">
      <WishlistAppHeader />
      <p className="wishlist-loading">Loading…</p>
    </div>
  );

  if (error) return (
    <div className="app">
      <WishlistAppHeader />
      <ErrorScreen message={error} onRetry={loadWishlist} />
    </div>
  );

  return (
    <div className="app">
      <WishlistAppHeader count={plants.length} />

      {plants.length === 0 ? (
        <div className="wishlist-empty">
          <Icon name="star" size={28} />
          <p>Your wishlist is empty.</p>
          <p className="wishlist-empty-hint">
            Add a plant to track priority, target price, and shop details.
          </p>
        </div>
      ) : (
        <div className="plant-list">
          {plants.map(plant => (
            <div
              key={plant.id}
              className={`plant-card wishlist-card ${plant.latest_photo_id ? 'plant-card-photo' : ''}`}
            >
              {/* Stretched overlay link — covers the card but sits below actions */}
              <Link
                to={`/plants/${plant.id}`}
                state={{ fromWishlist: true }}
                className="wishlist-card-overlay"
                aria-label={`View ${plantDisplayName(plant)}`}
              />
              {plant.latest_photo_id && (
                <div className="card-photo">
                  <img
                    src={photoUrl(plant.latest_photo_id, plant.latest_photo_rotation_version, plant.latest_photo_created_at)}
                    alt={plantDisplayName(plant) || plant.specimen_id}
                    className="card-photo-img"
                    loading="lazy"
                    onLoad={e => e.currentTarget.classList.add('loaded')}
                  />
                  {plant.photo_count > 1 && (
                    <span className="card-photo-count">{plant.photo_count}</span>
                  )}
                </div>
              )}
              <div className="card-body">
                <div className="plant-card-header">
                  <div>
                    <div className="plant-card-title">
                      {plantDisplayName(plant)}
                    </div>
                    <div className="plant-card-meta">
                      {plant.specimen_id || '—'}
                    </div>
                    <div className="plant-card-meta">
                      {formatWishlistPriority(plant.wishlist_priority)}
                    </div>
                  </div>
                  <div className="wishlist-card-actions">
                    <Link
                      to={`/plants/${plant.id}/edit`}
                      className="btn btn-secondary btn-sm"
                    >
                      Edit
                    </Link>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => setConversionTarget(plant)}
                    >
                      Mark acquired
                    </button>
                  </div>
                </div>

                  <div className="plant-card-row">
                    <strong>Shop</strong>
                    <span>{plant.shop_name || '—'}</span>
                  </div>
                  <div className="plant-card-row">
                    <strong>Listing</strong>
                    <span>
                      {plant.wishlist_url ? (
                        <a href={plant.wishlist_url} target="_blank" rel="noreferrer" className="wishlist-external-link">
                          View listing
                        </a>
                      ) : '—'}
                    </span>
                  </div>
                  <div className="plant-card-row">
                    <strong>Target price</strong>
                    <span>{plant.target_price != null ? formatCurrency(plant.target_price) : '—'}</span>
                  </div>
                  <div className="plant-card-row">
                    <strong>Notes</strong>
                    <span>{plant.acquisition_notes || plant.notes || '—'}</span>
                  </div>
                </div>
              </div>
          ))}
        </div>
      )}

      {conversionTarget && (
        <MarkAcquiredModal
          plant={conversionTarget}
          onSuccess={() => { loadWishlist(); setConversionTarget(null); }}
          onCancel={() => setConversionTarget(null)}
        />
      )}
    </div>
  );
}

export default Wishlist;
